import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import colaboradoresService from '../../services/colaboradoresService';
import { regionesComunas } from '../../constants/regionesComunas';
import { OCUPACIONES } from '../../utils/licenciasMedicas';
import '../../styles/pages/RRHH/ColaboradoresPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de gestión de Colaboradores (Empleados)
 * Preparada para uso manual y por agentes MCP
 */
const ColaboradoresPage = ({ token, id_company }) => {
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('ACTIVO');
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [colaboradorActual, setColaboradorActual] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const pdfInputRef = useRef(null);

    const [comunasDisponibles, setComunasDisponibles] = useState([]);

    // Formulario
    const [formData, setFormData] = useState({
        rut: '',
        nombres: '',
        apellido_paterno: '',
        apellido_materno: '',
        email: '',
        telefono: '',
        direccion: '',
        comuna: '',
        region: '',
        fecha_ingreso: '',
        cargo: '',
        sueldo_base: '',
        afp: '',
        isapre_fonasa: '',
        plan_isapre_uf: '',
        // Nuevos campos
        sexo: '',
        nacionalidad: '',
        jornada: '',
        tipo_contrato: '',
        subsidio_joven: false,
        tipo_trabajador: 'ACTIVO',
        gratificacion: '',
        // Datos que pide el portal de licencias médicas
        ocupacion_lme: '',
        fecha_afiliacion_previsional: ''
    });

    const fetchColaboradores = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, estado: filtroEstado },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setColaboradores(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
            Swal.fire('Error', 'No se pudieron cargar los colaboradores', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, token, filtroEstado]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const url = modoEdicion
                ? `${API_BASE_URL}/api/rrhh/colaboradores/${colaboradorActual.id}`
                : `${API_BASE_URL}/api/rrhh/colaboradores`;

            const method = modoEdicion ? 'put' : 'post';

            const response = await axios[method](url, {
                ...formData,
                id_company
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                Swal.fire('Éxito', `Colaborador ${modoEdicion ? 'actualizado' : 'creado'} correctamente`, 'success');
                setShowModal(false);
                resetForm();
                fetchColaboradores();
            }
        } catch (error) {
            console.error('Error al guardar colaborador:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    // Función auxiliar para formatear fecha a YYYY-MM-DD (formato requerido por input type="date")
    const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    const handleEdit = (colaborador) => {
        setModoEdicion(true);
        setColaboradorActual(colaborador);

        // Si tiene región, cargar las comunas correspondientes
        if (colaborador.region && regionesComunas[colaborador.region]) {
            setComunasDisponibles(regionesComunas[colaborador.region]);
        }

        setFormData({
            rut: colaborador.rut || '',
            nombres: colaborador.nombres || '',
            apellido_paterno: colaborador.apellido_paterno || '',
            apellido_materno: colaborador.apellido_materno || '',
            email: colaborador.email || '',
            telefono: colaborador.telefono || '',
            direccion: colaborador.direccion || '',
            comuna: colaborador.comuna || '',
            region: colaborador.region || '',
            fecha_ingreso: formatDateForInput(colaborador.fecha_ingreso),
            cargo: colaborador.cargo || '',
            sueldo_base: colaborador.sueldo_base || '',
            afp: colaborador.afp || '',
            isapre_fonasa: colaborador.isapre_fonasa || '',
            plan_isapre_uf: colaborador.plan_isapre_uf || '',
            // Nuevos campos
            sexo: colaborador.sexo || '',
            nacionalidad: colaborador.nacionalidad || '',
            jornada: colaborador.jornada || '',
            tipo_contrato: colaborador.tipo_contrato || '',
            subsidio_joven: colaborador.subsidio_joven || false,
            tipo_trabajador: colaborador.tipo_trabajador || 'ACTIVO',
            gratificacion: colaborador.gratificacion || '',
            ocupacion_lme: colaborador.ocupacion_lme ?? '',
            fecha_afiliacion_previsional: formatDateForInput(colaborador.fecha_afiliacion_previsional)
        });
        setShowModal(true);
    };

    const handleDelete = async (id, nombreCompleto) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Se eliminará permanentemente a ${nombreCompleto} y todos sus registros relacionados (contratos, liquidaciones, finiquitos, licencias médicas). Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await colaboradoresService.deleteColaborador(id, id_company, token);
                Swal.fire('Eliminado', `${nombreCompleto} ha sido eliminado correctamente`, 'success');
                fetchColaboradores();
            } catch (error) {
                console.error('Error al eliminar colaborador:', error);
                Swal.fire('Error', error.message || 'Error al eliminar colaborador', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            rut: '',
            nombres: '',
            apellido_paterno: '',
            apellido_materno: '',
            email: '',
            telefono: '',
            direccion: '',
            comuna: '',
            region: '',
            fecha_ingreso: '',
            cargo: '',
            sueldo_base: '',
            afp: '',
            isapre_fonasa: '',
            plan_isapre_uf: '',
            // Nuevos campos
            sexo: '',
            nacionalidad: '',
            jornada: '',
            tipo_contrato: '',
            subsidio_joven: false,
            tipo_trabajador: 'ACTIVO',
            gratificacion: '',
            ocupacion_lme: '',
            fecha_afiliacion_previsional: ''
        });
        setComunasDisponibles([]);
        setModoEdicion(false);
        setColaboradorActual(null);
    };

    const handleCargarPdf = () => {
        pdfInputRef.current.click();
    };

    const handlePdfSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            Swal.fire('Error', 'El archivo debe ser un PDF', 'error');
            return;
        }

        try {
            setLoadingPdf(true);

            Swal.fire({
                title: 'Extrayendo datos del PDF...',
                html: 'Claude está analizando el documento. Esto puede tomar unos segundos.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const result = await colaboradoresService.extractFromPdf(file, token);

            Swal.close();

            if (result.success) {
                const fieldsCount = result.fieldsExtracted.length;
                const totalFields = result.fieldsExtracted.length + result.fieldsMissing.length;

                await Swal.fire({
                    title: 'Datos extraídos',
                    html: `
                        <p><strong>${fieldsCount} de ${totalFields} campos</strong> fueron extraídos del PDF.</p>
                        <p class="text-muted" style="font-size: 14px; margin-top: 10px;">
                            Los campos faltantes quedarán en blanco para que los completes manualmente.
                        </p>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });

                const extractedData = result.data;

                // Si hay región, cargar comunas
                if (extractedData.region && regionesComunas[extractedData.region]) {
                    setComunasDisponibles(regionesComunas[extractedData.region]);
                }

                // Mapear sexo M/F → Masculino/Femenino
                let sexoMapeado = extractedData.sexo || '';
                if (sexoMapeado === 'M') sexoMapeado = 'Masculino';
                if (sexoMapeado === 'F') sexoMapeado = 'Femenino';

                // Actualizar formulario con todos los campos extraídos
                setFormData(prev => ({
                    ...prev,
                    rut: extractedData.rut || '',
                    nombres: extractedData.nombres || '',
                    apellido_paterno: extractedData.apellido_paterno || '',
                    apellido_materno: extractedData.apellido_materno || '',
                    email: extractedData.email || '',
                    telefono: extractedData.telefono || '',
                    direccion: extractedData.direccion || '',
                    comuna: extractedData.comuna || '',
                    region: extractedData.region || '',
                    fecha_ingreso: extractedData.fecha_ingreso || '',
                    cargo: extractedData.cargo || '',
                    sueldo_base: extractedData.sueldo_base || '',
                    afp: extractedData.afp || '',
                    isapre_fonasa: extractedData.isapre_fonasa || '',
                    plan_isapre_uf: extractedData.plan_isapre_uf || '',
                    sexo: sexoMapeado,
                    nacionalidad: extractedData.nacionalidad || '',
                    jornada: extractedData.jornada || '',
                    tipo_contrato: extractedData.tipo_contrato || '',
                    subsidio_joven: extractedData.subsidio_joven || false,
                    tipo_trabajador: extractedData.tipo_trabajador || 'ACTIVO',
                    gratificacion: extractedData.gratificacion || ''
                }));

                setShowModal(true);

            } else {
                Swal.fire('Error', result.message || 'No se pudo extraer datos del PDF', 'error');
            }

        } catch (error) {
            console.error('Error al procesar PDF:', error);
            Swal.fire('Error', error.message || 'Error al procesar el PDF', 'error');
        } finally {
            setLoadingPdf(false);
            e.target.value = '';
        }
    };

    return (
        <div className="colaboradores-container">
            <div className="colaboradores-header">
                <h1>Gestión de Colaboradores</h1>
                <div className="header-buttons">
                    <button
                        className="btn-secondary"
                        onClick={handleCargarPdf}
                        title="Cargar datos desde PDF (CV, Contrato, etc.) usando IA"
                        disabled={loadingPdf}
                    >
                        <FaPlus /> {loadingPdf ? 'Cargando...' : 'Cargar desde PDF'}
                    </button>
                    <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                        <FaPlus /> Nuevo Colaborador
                    </button>
                    {/* Input file oculto para PDFs */}
                    <input
                        type="file"
                        ref={pdfInputRef}
                        accept="application/pdf"
                        onChange={handlePdfSelected}
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            {/* Filtro */}
            <div className="colaboradores-filters">
                <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                    <option value="">Todos</option>
                    <option value="ACTIVO">Activos</option>
                    <option value="DESPEDIDO">Despedidos</option>
                </select>
            </div>

            {/* Tabla de colaboradores */}
            {loading ? (
                <div className="loading">Cargando colaboradores...</div>
            ) : (
                <div className="colaboradores-table-container">
                    <table className="colaboradores-table">
                        <thead>
                            <tr>
                                <th>RUT</th>
                                <th>Nombre Completo</th>
                                <th>Cargo</th>
                                <th>Fecha Ingreso</th>
                                <th>Sueldo Base</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {colaboradores.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron colaboradores
                                    </td>
                                </tr>
                            ) : (
                                colaboradores.map((col) => (
                                    <tr key={col.id}>
                                        <td>{col.rut}</td>
                                        <td>{`${col.nombres} ${col.apellido_paterno} ${col.apellido_materno || ''}`}</td>
                                        <td>{col.cargo}</td>
                                        <td>{new Date(col.fecha_ingreso).toLocaleDateString('es-CL')}</td>
                                        <td>${parseInt(col.sueldo_base).toLocaleString('es-CL')}</td>
                                        <td>
                                            <span className={`badge badge-${col.estado.toLowerCase()}`}>
                                                {col.estado}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn-edit" onClick={() => handleEdit(col)} title="Editar">
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="btn-delete"
                                                onClick={() => handleDelete(col.id, `${col.nombres} ${col.apellido_paterno} ${col.apellido_materno || ''}`.trim())}
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content colaboradores-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{modoEdicion ? 'Editar Colaborador' : 'Nuevo Colaborador'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>RUT *</label>
                                    <input
                                        type="text"
                                        value={formData.rut}
                                        onChange={(e) => setFormData({...formData, rut: e.target.value})}
                                        required
                                        disabled={modoEdicion}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Nombres *</label>
                                    <input
                                        type="text"
                                        value={formData.nombres}
                                        onChange={(e) => setFormData({...formData, nombres: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Apellido Paterno *</label>
                                    <input
                                        type="text"
                                        value={formData.apellido_paterno}
                                        onChange={(e) => setFormData({...formData, apellido_paterno: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Apellido Materno</label>
                                    <input
                                        type="text"
                                        value={formData.apellido_materno}
                                        onChange={(e) => setFormData({...formData, apellido_materno: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Teléfono</label>
                                    <input
                                        type="text"
                                        value={formData.telefono}
                                        onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Dirección</label>
                                    <input
                                        type="text"
                                        value={formData.direccion}
                                        onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Región</label>
                                    <select
                                        value={formData.region}
                                        onChange={(e) => {
                                            const nuevaRegion = e.target.value;
                                            setFormData({...formData, region: nuevaRegion, comuna: ''});
                                            setComunasDisponibles(regionesComunas[nuevaRegion] || []);
                                        }}
                                    >
                                        <option value="">Seleccionar región...</option>
                                        {Object.keys(regionesComunas).map(region => (
                                            <option key={region} value={region}>{region}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Comuna</label>
                                    <select
                                        value={formData.comuna}
                                        onChange={(e) => setFormData({...formData, comuna: e.target.value})}
                                        disabled={!formData.region}
                                    >
                                        <option value="">Seleccionar comuna...</option>
                                        {comunasDisponibles.map(comuna => (
                                            <option key={comuna} value={comuna}>{comuna}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Cargo *</label>
                                    <input
                                        type="text"
                                        value={formData.cargo}
                                        onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Fecha Ingreso *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_ingreso}
                                        onChange={(e) => setFormData({...formData, fecha_ingreso: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sueldo Base *</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={formData.sueldo_base === '' ? '' : Number(formData.sueldo_base).toLocaleString('es-CL')}
                                        onChange={(e) => setFormData({...formData, sueldo_base: e.target.value.replace(/\D/g, '')})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>AFP</label>
                                    <select
                                        value={formData.afp}
                                        onChange={(e) => setFormData({...formData, afp: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="CAPITAL">AFP Capital</option>
                                        <option value="CUPRUM">AFP Cuprum</option>
                                        <option value="HABITAT">AFP Habitat</option>
                                        <option value="PLANVITAL">AFP PlanVital</option>
                                        <option value="PROVIDA">AFP Provida</option>
                                        <option value="MODELO">AFP Modelo</option>
                                        <option value="UNO">AFP UNO</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Salud (Isapre/Fonasa)</label>
                                    <select
                                        value={formData.isapre_fonasa}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            isapre_fonasa: e.target.value,
                                            // En Fonasa el plan en UF no aplica.
                                            plan_isapre_uf: e.target.value === 'FONASA' ? '' : formData.plan_isapre_uf
                                        })}
                                    >
                                        {/* Los valores son las glosas exactas de
                                            catalogo_institucion_salud, para que el TXT de
                                            Previred resuelva el código sin búsqueda parcial. */}
                                        <option value="">Seleccionar...</option>
                                        <option value="FONASA">Fonasa</option>
                                        <option value="BANMEDICA">Banmédica</option>
                                        <option value="COLMENA">Colmena</option>
                                        <option value="CONSALUD">Consalud</option>
                                        <option value="CRUZ DEL NORTE">Cruz del Norte</option>
                                        <option value="ESENCIAL">Esencial S.A.</option>
                                        <option value="FUNDACION">Fundación</option>
                                        <option value="ISALUD">Isalud</option>
                                        <option value="CRUZ BLANCA">Isapre Cruz Blanca S.A.</option>
                                        <option value="NUEVA MASVIDA">Nueva Masvida</option>
                                        <option value="VIDA TRES">VidaTres</option>
                                    </select>
                                </div>
                                {/* Las isapres cobran en UF. Solo aparece cuando hay una
                                    elegida: en Fonasa la cotización es el 7% por ley. */}
                                {formData.isapre_fonasa && formData.isapre_fonasa !== 'FONASA' && (
                                    <div className="form-group">
                                        <label>Plan Isapre (UF)</label>
                                        <input
                                            type="number"
                                            step="0.0001"
                                            min="0"
                                            placeholder="Ej: 3,5"
                                            value={formData.plan_isapre_uf}
                                            onChange={(e) => setFormData({...formData, plan_isapre_uf: e.target.value})}
                                        />
                                    </div>
                                )}

                                {/* Nuevos campos */}
                                <div className="form-group">
                                    <label>Sexo</label>
                                    <select
                                        value={formData.sexo}
                                        onChange={(e) => setFormData({...formData, sexo: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="M">Masculino</option>
                                        <option value="F">Femenino</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Nacionalidad</label>
                                    <select
                                        value={formData.nacionalidad}
                                        onChange={(e) => setFormData({...formData, nacionalidad: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="CHILENA">Chilena</option>
                                        <option value="EXTRANJERA">Extranjera</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Contrato</label>
                                    <select
                                        value={formData.tipo_contrato}
                                        onChange={(e) => setFormData({...formData, tipo_contrato: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="INDEFINIDO">Indefinido</option>
                                        <option value="PLAZO_FIJO">Plazo Fijo</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Jornada</label>
                                    <select
                                        value={formData.jornada}
                                        onChange={(e) => setFormData({...formData, jornada: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="COMPLETA">Completa</option>
                                        <option value="PARCIAL">Parcial</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Trabajador</label>
                                    <select
                                        value={formData.tipo_trabajador}
                                        onChange={(e) => setFormData({...formData, tipo_trabajador: e.target.value})}
                                    >
                                        <option value="ACTIVO">Activo (No pensionado)</option>
                                        <option value="PENSIONADO_COTIZA">Pensionado y Cotiza</option>
                                        <option value="PENSIONADO_NO_COTIZA">Pensionado y No cotiza o Pensionado por Invalidez Total</option>
                                        <option value="ACTIVO_65">Activo mayor a 65 años (nunca pensionado)</option>
                                        <option value="EXENTO">Exento de cotizar (Mujer mayor de 60 años u hombre mayor de 65 o Extranjero)</option>
                                        <option value="INVALIDEZ_PARCIAL">Pensionado por Invalidez Parcial</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Gratificación</label>
                                    <select
                                        value={formData.gratificacion}
                                        onChange={(e) => setFormData({...formData, gratificacion: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="articulo 47">Artículo 47</option>
                                        <option value="articulo 50">Artículo 50</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Ocupación (licencias médicas)</label>
                                    <select
                                        value={formData.ocupacion_lme}
                                        onChange={(e) => setFormData({...formData, ocupacion_lme: e.target.value})}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {OCUPACIONES.map(ocupacion => (
                                            <option key={ocupacion.valor} value={ocupacion.valor}>
                                                {ocupacion.valor} - {ocupacion.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fecha de afiliación previsional</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_afiliacion_previsional}
                                        onChange={(e) => setFormData({...formData, fecha_afiliacion_previsional: e.target.value})}
                                    />
                                </div>

                                <div className="form-group" style={{gridColumn: '1 / -1'}}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.subsidio_joven}
                                            onChange={(e) => setFormData({...formData, subsidio_joven: e.target.checked})}
                                        />
                                        {' '}Subsidio Joven
                                    </label>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColaboradoresPage;
